// src/components/Properties/TextProperties/TextProperties.tsx
import React from 'react';
import { Type, Trash2, AlignLeft, TextQuote } from 'lucide-react';
import { useTextBox } from 'src/hooks/useTextBox';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { useScene } from 'src/hooks/useScene';
import { ProjectionOrientationEnum } from 'src/types';
import { ColorPicker } from '../ColorPicker';

interface Props {
  id: string;
}

export const TextProperties: React.FC<Props> = ({ id }) => {
  const uiStateActions = useUiStateStore((state) => state.actions);
  const textBox = useTextBox(id);
  const { updateTextBox, deleteTextBox } = useScene();

  // Handle null textBox
  if (!textBox) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Type className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            Text Not Found
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            The selected text element no longer exists
          </p>
        </div>
      </div>
    );
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this text element?')) {
      uiStateActions.setItemControls(null);
      deleteTextBox(textBox.id);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-4">
        {/* Text Content Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <TextQuote className="h-4 w-4 text-gray-500" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Text Content
            </label>
          </div>

          <div>
            <textarea
              value={textBox.content || ''}
              onChange={(e) =>
                updateTextBox(textBox.id, { content: e.target.value })
              }
              rows={3}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder-slate-400"
              placeholder="Enter your text..."
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The text that will be displayed on the diagram
            </p>
          </div>
        </div>

        {/* Appearance Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Type className="h-4 w-4 text-gray-500" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Appearance
            </label>
          </div>

          {/* Text Color */}
          <div>
            <ColorPicker
              value={textBox.color || '#1f2937'}
              onChange={(color) => updateTextBox(textBox.id, { color })}
              label="Text Color"
            />
          </div>

          {/* Font Size */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              Font Size: {((textBox.fontSize || 0.6) * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0.3"
              max="0.9"
              step="0.05"
              value={textBox.fontSize || 0.6}
              onChange={(e) =>
                updateTextBox(textBox.id, {
                  fontSize: parseFloat(e.target.value)
                })
              }
              className="w-full"
            />
            <div className="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>Small</span>
              <span>Large</span>
            </div>
          </div>

          {/* Text Orientation/Alignment */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlignLeft className="h-4 w-4 text-gray-500" />
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                Text Orientation
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() =>
                  updateTextBox(textBox.id, {
                    orientation: ProjectionOrientationEnum.X
                  })
                }
                className={`flex items-center justify-center gap-2 rounded-md border px-4 py-2.5 text-sm font-medium transition-colors ${
                  textBox.orientation === ProjectionOrientationEnum.X
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-gray-300 dark:hover:bg-slate-700'
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="transform rotate-[-30deg]"
                >
                  <path
                    d="M4 8h8M6 6l-2 2 2 2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                X-Axis
              </button>
              <button
                onClick={() =>
                  updateTextBox(textBox.id, {
                    orientation: ProjectionOrientationEnum.Y
                  })
                }
                className={`flex items-center justify-center gap-2 rounded-md border px-4 py-2.5 text-sm font-medium transition-colors ${
                  textBox.orientation === ProjectionOrientationEnum.Y
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-gray-300 dark:hover:bg-slate-700'
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="transform rotate-[30deg]"
                >
                  <path
                    d="M4 8h8M6 6l-2 2 2 2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Y-Axis
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Choose the isometric axis along which the text should be aligned
            </p>
          </div>
        </div>

        {/* Delete Action */}
        <div className="border-t border-gray-200 pt-4 dark:border-slate-600">
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};
